import { Table } from '../../atoms/Table';
import { Pagination } from '../../atoms/Pagination';
import { Stack } from '../../atoms/Stack';
import { Loader } from '../../atoms/Loader';
import { EmptyState } from '../EmptyState';
import type { CoreProps } from '../../types';
import { useShouldRender } from '../../../hooks/useShouldRender';

export interface DataTableColumn<T> {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  width?: string | number;
}

export interface DataTableProps<T> extends CoreProps {
  columns: DataTableColumn<T>[];
  rows: T[];
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  getRowId: (row: T) => string;
  onRowClick?: (row: T) => void;
}

export function DataTable<T>({
  columns,
  rows,
  loading,
  emptyTitle = 'No data',
  emptyDescription,
  page = 1,
  totalPages = 1,
  onPageChange,
  getRowId,
  onRowClick,
  testId,
  view = 'all',
}: DataTableProps<T>) {
  const shouldRender = useShouldRender(view);
  if (!shouldRender) return null;

  if (loading) {
    return (
      <Stack align="center" py="xl" data-testid={testId}>
        <Loader />
      </Stack>
    );
  }

  if (!rows.length) {
    return (
      <EmptyState title={emptyTitle} description={emptyDescription} testId={testId} />
    );
  }

  return (
    <Stack gap="md" data-testid={testId}>
      <Table.ScrollContainer minWidth={600}>
        <Table highlightOnHover striped>
          <Table.Thead>
            <Table.Tr>
              {columns.map((col) => (
                <Table.Th key={col.key} w={col.width}>
                  {col.header}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr
                key={getRowId(row)}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((col) => (
                  <Table.Td key={col.key}>{col.render(row)}</Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {totalPages > 1 && onPageChange ? (
        <Pagination value={page} onChange={onPageChange} total={totalPages} />
      ) : null}
    </Stack>
  );
}
